// Convierte los eventos del servidor en frases para el dueño ("Juan cambió el precio de…")
// y agrupa los cambios masivos (aumentos, importaciones) en una sola línea desplegable.
import { EMPLOYEE_PERMISSIONS, PAYMENT_METHODS, formatQty, normalizePermissions, variantLabel, type UnitType } from '@pos/core';

export interface ActivityItem {
  at: string;
  key: string;
  user_id: string | null;
  device_id: string | null;
  kind: string;
  data: Record<string, unknown>;
}

export interface ActivityContext {
  userName: (userId: string | null) => string;
  deviceName: (deviceId: string | null) => string | null;
  membershipName: (membershipId: string) => string | null;
  money: (cents: number) => string;
}

export interface ActivityLine {
  key: string;
  at: Date;
  userId: string | null;
  who: string;
  text: string;
  device: string | null;
  /** Cambios agrupados (aumento masivo, importación). Vacío si es un solo evento. */
  children: ActivityLine[];
}

const str = (v: unknown) => (typeof v === 'string' ? v : '');
const num = (v: unknown) => (typeof v === 'number' ? v : Number(v ?? 0));

function productLabel(data: Record<string, unknown>): string {
  const variant = variantLabel(data.variant as Record<string, string> | null);
  return `“${str(data.product)}${variant ? ` · ${variant}` : ''}”`;
}

const PAYMENT_LABEL: Record<string, string> = Object.fromEntries(Object.entries(PAYMENT_METHODS).map(([k, v]) => [k, v.label.toLowerCase()]));

const PERMISSION_LABEL = new Map<string, string>(EMPLOYEE_PERMISSIONS.map((p) => [p.key, p.label.toLowerCase()]));

function membershipChange(data: Record<string, unknown>): string {
  const before = (data.before ?? {}) as Record<string, unknown>;
  const after = (data.after ?? {}) as Record<string, unknown>;
  const name = str(data.display_name) || 'un empleado';
  const parts: string[] = [];
  if (before.active !== after.active) parts.push(after.active ? `reactivó a ${name}` : `desactivó a ${name}`);
  if (before.username !== after.username) parts.push(`cambió el usuario de ${name} a “${str(after.username)}”`);
  const p0 = normalizePermissions(before.permissions) as Record<string, unknown>;
  const p1 = normalizePermissions(after.permissions) as Record<string, unknown>;
  const added = [...PERMISSION_LABEL.keys()].filter((k) => p1[k] && !p0[k]).map((k) => PERMISSION_LABEL.get(k)!);
  const removed = [...PERMISSION_LABEL.keys()].filter((k) => p0[k] && !p1[k]).map((k) => PERMISSION_LABEL.get(k)!);
  if (added.length || removed.length) {
    const changes = [added.length && `ahora puede ${added.join(', ')}`, removed.length && `ya no puede ${removed.join(', ')}`].filter(Boolean);
    parts.push(`cambió los permisos de ${name}: ${changes.join('; ')}`);
  } else if (p0.max_discount_pct !== p1.max_discount_pct) {
    parts.push(`cambió el descuento máximo de ${name} a ${num(p1.max_discount_pct)} %`);
  }
  return parts.join(' y ') || `modificó a ${name}`;
}

function qty(d: Record<string, unknown>, signed = false): string {
  const q = num(d.qty);
  const text = formatQty(Math.abs(q), (str(d.unit_type) || 'unit') as UnitType);
  return signed ? `${q > 0 ? '+' : '−'}${text}` : text;
}

const reason = (d: Record<string, unknown>) => (d.reason ? ` · motivo: ${str(d.reason)}` : '');

export function describeItem(item: ActivityItem, ctx: ActivityContext): string {
  const d = item.data;
  switch (item.kind) {
    case 'price.changed':
      return `cambió el precio de ${productLabel(d)}: ${ctx.money(num(d.old))} → ${ctx.money(num(d.new))}`;
    case 'price.set':
      return `cargó ${productLabel(d)} a ${ctx.money(num(d.new))}`;
    case 'cost.changed':
      return `cambió el costo de ${productLabel(d)}: ${ctx.money(num(d.old))} → ${ctx.money(num(d.new))}`;
    case 'cost.set':
      return `cargó el costo de ${productLabel(d)}: ${ctx.money(num(d.new))}`;
    case 'membership.updated':
      return membershipChange(d);
    case 'employee.created':
      return `dio de alta a ${str(d.display_name) || 'un empleado'}${d.username ? ` (usuario “${str(d.username)}”)` : ''}`;
    case 'employee.pin_changed':
      return `cambió el PIN de ${ctx.membershipName(str(d.entity_id)) ?? 'un empleado'}`;
    case 'device.enrolled':
      return `habilitó el dispositivo “${str(d.name)}”`;
    case 'device.revoked':
      return `deshabilitó el dispositivo “${str(d.name)}”`;
    case 'business.created':
      return 'creó el negocio';
    case 'stock.initial':
      return `cargó stock inicial de ${productLabel(d)}: ${qty(d)}`;
    case 'stock.adjustment':
      return `ajustó el stock de ${productLabel(d)}: ${qty(d, true)}${reason(d)}`;
    case 'stock.shrinkage':
      return `registró una merma de ${productLabel(d)}: ${qty(d)}${reason(d)}`;
    case 'stock.count':
      return `contó ${productLabel(d)} (diferencia ${qty(d, true)})`;
    case 'stock.supplier_return':
      return `devolvió al proveedor ${qty(d)} de ${productLabel(d)}${reason(d)}`;
    case 'purchase.created': {
      const items = num(d.items);
      return (
        `cargó una compra${d.supplier ? ` de ${str(d.supplier)}` : ''}${d.invoice ? ` (${str(d.invoice)})` : ''}` +
        `: ${items} producto${items === 1 ? '' : 's'}${d.total != null ? ` por ${ctx.money(num(d.total))}` : ''}`
      );
    }
    case 'purchase.voided':
      return `anuló una compra${d.supplier ? ` de ${str(d.supplier)}` : ''}${reason(d)}`;
    case 'sale.returned': {
      const items = num(d.items);
      return `hizo una devolución de ${ctx.money(num(d.total))} (${items} producto${items === 1 ? '' : 's'}) de la venta ${str(d.original_number)}`;
    }
    case 'cash.closed': {
      const diff = num(d.difference);
      const result = d.difference == null ? '' : diff === 0 ? ' sin diferencia' : diff < 0 ? ` con un faltante de ${ctx.money(-diff)}` : ` con un sobrante de ${ctx.money(diff)}`;
      return `cerró la caja${result} (contó ${ctx.money(num(d.counted))}${d.expected != null ? ` y debía haber ${ctx.money(num(d.expected))}` : ''})${d.notes ? ` · “${str(d.notes)}”` : ''}`;
    }
    case 'cash.in':
      return `ingresó ${ctx.money(num(d.amount))} a la caja${reason(d)}`;
    case 'cash.out':
      return `retiró ${ctx.money(num(d.amount))} de la caja${reason(d)}`;
    case 'cash.expense':
      return `pagó un gasto de ${ctx.money(num(d.amount))} con la caja${reason(d)}`;
    case 'account.payment':
      return `registró un pago de ${ctx.money(-num(d.amount))} de ${str(d.customer)}${d.method ? ` (${PAYMENT_LABEL[str(d.method)] ?? str(d.method)})` : ''}`;
    case 'account.adjustment':
      return `ajustó la cuenta de ${str(d.customer)}: ${num(d.amount) > 0 ? '+' : '−'}${ctx.money(Math.abs(num(d.amount)))}${d.note ? ` · motivo: ${str(d.note)}` : ''}`;
    case 'sale.discounted': {
      const pct = num(d.subtotal) > 0 ? Math.round((num(d.discount) / num(d.subtotal)) * 100) : 0;
      return `vendió con descuento: ${ctx.money(num(d.discount))} (${pct} %) en la venta ${str(d.number)} de ${ctx.money(num(d.total))}`;
    }
    default:
      return item.kind;
  }
}

/** Frase para un grupo de cambios masivos del mismo tipo. */
const GROUP_TEXT: Record<string, (n: number) => string> = {
  'price.changed': (n) => `cambió ${n} precios`,
  'price.set': (n) => `cargó ${n} productos`,
  'cost.changed': (n) => `cambió ${n} costos`,
  'cost.set': (n) => `cargó ${n} costos`,
  'stock.count': (n) => `registró una toma de inventario con ${n} diferencias`,
  'stock.initial': (n) => `cargó stock inicial de ${n} productos`,
  'stock.shrinkage': (n) => `registró ${n} mermas`,
};

/** Cambios del mismo tipo y persona a menos de este tiempo se muestran juntos. */
const GROUP_WINDOW_MS = 10 * 60_000;

export function buildLines(items: ActivityItem[], ctx: ActivityContext): ActivityLine[] {
  const lines: ActivityLine[] = [];
  let group: { kind: string; line: ActivityLine } | null = null;

  for (const item of items) {
    const line: ActivityLine = {
      key: item.key,
      at: new Date(item.at),
      userId: item.user_id,
      who: ctx.userName(item.user_id),
      text: describeItem(item, ctx),
      device: ctx.deviceName(item.device_id),
      children: [],
    };
    const groupable = item.kind in GROUP_TEXT;
    if (
      groupable &&
      group &&
      group.kind === item.kind &&
      group.line.userId === item.user_id &&
      group.line.at.getTime() - line.at.getTime() <= GROUP_WINDOW_MS
    ) {
      const g = group.line;
      if (g.children.length === 0) g.children.push({ ...g, children: [] });
      g.children.push(line);
      g.text = GROUP_TEXT[item.kind]!(g.children.length);
      continue;
    }
    lines.push(line);
    group = groupable ? { kind: item.kind, line } : null;
  }
  return lines;
}

/** "Hoy", "Ayer" o "lunes 29 de septiembre". */
export function dayLabel(date: Date, now = new Date()): string {
  const day = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const diff = Math.round((day(now) - day(date)) / 86_400_000);
  if (diff === 0) return 'Hoy';
  if (diff === 1) return 'Ayer';
  return new Intl.DateTimeFormat('es-AR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    ...(date.getFullYear() !== now.getFullYear() && { year: 'numeric' }),
  }).format(date);
}
